import { useEffect, useRef, useState } from "react";
import { CaretDownIcon } from "../../../assets/icons";
import styles from "./Select.module.scss";

export type SelectOption = {
    value: string;
    label: string;
};

type Props = {
    label: string;
    options: SelectOption[];
    value?: string;
    values?: string[];
    multiple?: boolean;
    error?: string;
    required?: boolean;
    disabled?: boolean;
    onChange?: (option: SelectOption) => void;
    onChangeMulti?: (options: SelectOption[]) => void;
};

const Select = ({ label, options, value, values, multiple, error, required, disabled, onChange, onChangeMulti }: Props) => {
    const [open, setOpen] = useState(false);
    const ref = useRef<HTMLDivElement>(null);

    const selected = options.find((o) => o.value === value);
    const selectedMulti = multiple ? (values ?? []).map(v => options.find(o => o.value === v)).filter((o): o is SelectOption => !!o) : [];

    useEffect(() => {
        const handleClickOutside = (e: MouseEvent) => {
            if (ref.current && !ref.current.contains(e.target as Node)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const handleSelect = (option: SelectOption) => {
        if (multiple) {
            const currentValues = values ?? [];
            const next = currentValues.includes(option.value)
                ? options.filter(o => currentValues.includes(o.value) && o.value !== option.value)
                : options.filter(o => currentValues.includes(o.value) || o.value === option.value);
            onChangeMulti?.(next);
        } else {
            onChange?.(option);
            setOpen(false);
        }
    };

    const displayText = multiple
        ? selectedMulti.length === 0
            ? `Select ${label.toLowerCase()}`
            : selectedMulti.length === 1
            ? selectedMulti[0].label
            : `${selectedMulti[0].label} +${selectedMulti.length - 1}`
        : selected
        ? selected.label
        : `Select ${label.toLowerCase()}`;

    const hasSelection = multiple ? selectedMulti.length > 0 : !!selected;

    return (
        <div className={styles.wrapper} ref={ref}>
            <label className={styles.label}>
                {label}
                {required && <span className={styles.required}>*</span>}
            </label>
            <div className={styles.inputErrorContainer}>
                <div
                    className={`${styles.inputContainer} ${error ? styles.errorBorder : ""} ${open ? styles.active : ""} ${disabled ? styles.disabled : ""}`}
                    onClick={() => !disabled && setOpen((prev) => !prev)}
                >
                    <span className={`${styles.value} ${!hasSelection ? styles.placeholder : ""}`}>
                        {displayText}
                    </span>
                    <CaretDownIcon className={`${styles.caret} ${open ? styles.caretOpen : ""}`} />

                    {open && (
                        <div className={styles.dropdown}>
                            {options.map((opt) => {
                                const isSelected = multiple ? (values ?? []).includes(opt.value) : opt.value === value;
                                return (
                                    <div
                                        key={opt.value}
                                        className={`${styles.option} ${isSelected ? styles.optionSelected : ""}`}
                                        onClick={(e) => { e.stopPropagation(); handleSelect(opt); }}
                                    >
                                        {opt.label}
                                        {isSelected && <span className={styles.checkmark}>✓</span>}
                                    </div>
                                );
                            })}
                        </div>
                    )}
                </div>
                {error && <span className={styles.error}>{error}</span>}
            </div>
        </div>
    );
};

export default Select;
